import { useState } from 'react';
import { AdminLayout } from '../../components/Layout/AdminLayout';
import { PageHeader } from '../../components/common';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import {
  Edit,
  Delete,
  MoreVert,
  Extension,
  CheckCircle,
  Cancel,
  Category,
  Business,
  Visibility,
  TrendingUp,
} from '@mui/icons-material';
import { useModules, useDeleteModule } from '../../hooks/useSuperAdmin';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { useQueryClient } from '@tanstack/react-query';
import { colors } from '../../theme/theme';

export const ModulesPage = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedModule, setSelectedModule] = useState<any>(null);

  const { modules, isLoading, error } = useModules(page + 1);
  const deleteModule = useDeleteModule();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, module: any) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedModule(module);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedModule(null);
  };

  const handleDelete = async () => {
    if (!selectedModule) return;

    if (window.confirm(`Are you sure you want to delete module "${selectedModule.name}"?`)) {
      try {
        await deleteModule.mutateAsync(selectedModule.id);
        showSnackbar('Module deleted successfully', 'success');
        queryClient.invalidateQueries({ queryKey: ['superadmin', 'modules'] });
        handleMenuClose();
      } catch (error) {
        showSnackbar('Failed to delete module', 'error');
      }
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Module Name',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Extension sx={{ fontSize: 18, color: colors.primary.main }} />
          <Box>
            <Box sx={{ fontWeight: 600, fontSize: '0.875rem' }}>{params.value}</Box>
            <Box sx={{ fontSize: '0.75rem', color: colors.grey[600] }}>
              {params.row.code || params.row.module_code || 'No code'}
            </Box>
          </Box>
        </Box>
      ),
    },
    {
      field: 'county',
      headerName: 'County',
      flex: 1,
      minWidth: 150,
      valueGetter: (value, row) => row.county?.name || 'N/A',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Business sx={{ fontSize: 16, color: colors.grey[500] }} />
          <span>{params.value}</span>
        </Box>
      ),
    },
    {
      field: 'module_type',
      headerName: 'Module Type',
      width: 150,
      valueGetter: (value, row) => row.moduleType?.name || row.module_type?.name || 'N/A',
      renderCell: (params) => (
        <Chip
          icon={<Category sx={{ fontSize: 14 }} />}
          label={params.value}
          size="small"
          variant="outlined"
          color="primary"
        />
      ),
    },
    {
      field: 'services_count',
      headerName: 'Services',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (value) => value || 0,
      renderCell: (params) => (
        <Chip
          label={params.value?.toLocaleString() || 0}
          size="small"
          color="info"
          variant="outlined"
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      renderCell: (params) => {
        const isActive = params.value === 'active';
        return (
          <Chip
            icon={isActive ? <CheckCircle sx={{ fontSize: 14 }} /> : <Cancel sx={{ fontSize: 14 }} />}
            label={isActive ? 'Active' : 'Inactive'}
            color={isActive ? 'success' : 'error'}
            size="small"
            sx={{ minWidth: 85 }}
          />
        );
      },
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 110,
      valueGetter: (value) => (value ? new Date(value).toLocaleDateString() : 'N/A'),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 70,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) => (
        <IconButton size="small" onClick={(e) => handleMenuOpen(e, params.row)}>
          <MoreVert />
        </IconButton>
      ),
    },
  ];

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title="Modules"
          subtitle="Manage platform modules and services"
          breadcrumbs={[
            { label: 'Dashboard', path: '/superadmin/dashboard' },
            { label: 'Modules' },
            { label: 'All Modules' },
          ]}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load modules. Please try again.
          </Alert>
        )}

        {/* Summary Cards */}
        {!isLoading && modules && (
          <Grid container spacing={2.5} mb={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.dark} 100%)`,
                  color: 'white',
                  borderRadius: 2.5,
                  border: `1px solid ${colors.primary.dark}`,
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 24px -10px ${colors.primary.main}80`,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ opacity: 0.95, fontSize: '0.875rem', fontWeight: 500 }}>Total Modules</Box>
                    <Extension sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Box sx={{ fontWeight: 700, fontSize: '2rem', mb: 0.5 }}>
                    {modules.meta?.total?.toLocaleString() || modules.data?.length || 0}
                  </Box>
                  <Box sx={{ opacity: 0.8, fontSize: '0.75rem' }}>Platform modules</Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  background: `linear-gradient(135deg, ${colors.success.main} 0%, ${colors.success.dark} 100%)`,
                  color: 'white',
                  borderRadius: 2.5,
                  border: `1px solid ${colors.success.dark}`,
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 24px -10px ${colors.success.main}80`,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ opacity: 0.95, fontSize: '0.875rem', fontWeight: 500 }}>Active Modules</Box>
                    <CheckCircle sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Box sx={{ fontWeight: 700, fontSize: '2rem', mb: 0.5 }}>
                    {modules.data?.filter((m: any) => m.status !== 'inactive').length || 0}
                  </Box>
                  <Box sx={{ opacity: 0.8, fontSize: '0.75rem' }}>Currently active</Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  background: `linear-gradient(135deg, ${colors.secondary.main} 0%, ${colors.secondary.dark} 100%)`,
                  color: 'white',
                  borderRadius: 2.5,
                  border: `1px solid ${colors.secondary.dark}`,
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 24px -10px ${colors.secondary.main}80`,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ opacity: 0.95, fontSize: '0.875rem', fontWeight: 500 }}>Counties</Box>
                    <Business sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Box sx={{ fontWeight: 700, fontSize: '2rem', mb: 0.5 }}>
                    {new Set(modules.data?.map((m: any) => m.county_id).filter(Boolean)).size || 0}
                  </Box>
                  <Box sx={{ opacity: 0.8, fontSize: '0.75rem' }}>With modules</Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  background: `linear-gradient(135deg, ${colors.info.main} 0%, ${colors.info.dark} 100%)`,
                  color: 'white',
                  borderRadius: 2.5,
                  border: `1px solid ${colors.info.dark}`,
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 24px -10px ${colors.info.main}80`,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ opacity: 0.95, fontSize: '0.875rem', fontWeight: 500 }}>Total Services</Box>
                    <TrendingUp sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Box sx={{ fontWeight: 700, fontSize: '2rem', mb: 0.5 }}>
                    {modules.data?.reduce((sum: number, m: any) => sum + (m.services_count || 0), 0) || 0}
                  </Box>
                  <Box sx={{ opacity: 0.8, fontSize: '0.75rem' }}>Across all modules</Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        <Card
          elevation={0}
          sx={{
            borderRadius: 2.5,
            overflow: 'hidden',
            border: `1px solid ${colors.grey[200]}`,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          }}
        >
          <CardContent sx={{ p: 0 }}>
            {isLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ width: '100%' }}>
                <DataGrid
                  rows={modules?.data || []}
                  columns={columns}
                  pageSizeOptions={[10, 25, 50, 100]}
                  paginationModel={{ page, pageSize }}
                  onPaginationModelChange={(model) => {
                    setPage(model.page);
                    setPageSize(model.pageSize);
                  }}
                  rowCount={modules?.meta?.total || modules?.data?.length || 0}
                  paginationMode={modules?.meta?.total ? 'server' : 'client'}
                  disableRowSelectionOnClick
                  autoHeight
                  slots={{ toolbar: GridToolbar }}
                  slotProps={{
                    toolbar: {
                      showQuickFilter: true,
                      quickFilterProps: { debounceMs: 500 },
                    },
                  }}
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-toolbarContainer': {
                      padding: '16px 20px',
                      borderBottom: `1px solid ${colors.grey[200]}`,
                      backgroundColor: colors.grey[50],
                    },
                    '& .MuiDataGrid-cell': {
                      borderBottom: `1px solid ${colors.grey[100]}`,
                      fontSize: '0.875rem',
                      py: 1.5,
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: colors.grey[50],
                      borderBottom: `2px solid ${colors.grey[300]}`,
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      color: colors.grey[700],
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    },
                    '& .MuiDataGrid-row': {
                      transition: 'background-color 0.15s ease',
                      '&:hover': {
                        backgroundColor: `${colors.primary.main}08`,
                        cursor: 'pointer',
                      },
                      '&:nth-of-type(even)': {
                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                      },
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: `2px solid ${colors.grey[200]}`,
                      backgroundColor: colors.grey[50],
                      minHeight: '56px',
                    },
                  }}
                />
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Actions Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem onClick={handleMenuClose}>
            <ListItemIcon>
              <Visibility fontSize="small" sx={{ color: colors.primary.main }} />
            </ListItemIcon>
            <ListItemText>View Details</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleMenuClose}>
            <ListItemIcon>
              <Edit fontSize="small" sx={{ color: colors.warning.main }} />
            </ListItemIcon>
            <ListItemText>Edit Module</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleDelete} sx={{ color: colors.error.main }}>
            <ListItemIcon>
              <Delete fontSize="small" sx={{ color: colors.error.main }} />
            </ListItemIcon>
            <ListItemText>Delete Module</ListItemText>
          </MenuItem>
        </Menu>
      </Box>
    </AdminLayout>
  );
};
